/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import { observer } from "mobx-react";
import dialerStoreData from "Stores/DialerData";
import DialerServiceData from "Stores/DialerServiceStore";
import { makeApi, URLS } from "Utils/apiURL";
import { checkTeamSubscriptionActive } from "Utils/commonAPI";
import { transactionAnimationState } from "Components/common/TransactionAnimation";
import { upgradePopupState } from "Components/Paywall/UpgradePopup";
import { buyCreditsPopupState } from "../../BuyCreditsPopup";
import { buyNewNumberPopupState } from "../../BuyNewNumberPopup";
import userDetail from "Stores/userDetail";
import Utils from "Utils/utils";
import countryData from "country-telephone-data";
import Flags from "Assets/png/flags.png";
import "react-telephone-input/css/default.css";

// ── helpers ───────────────────────────────────────────────────────────────────

const PAGE_SIZE = 10;
const STATUS_OPTIONS = ["Active", "Inactive"];

// numberType → display label + pill style
const NUMBER_TYPE_META = {
  TWILIO: { label: "Twilio", cls: "local" },
  VERIFIED: { label: "Verified", cls: "custom" },
  VOBIZ: { label: "India", cls: "vobiz" }
};

const getCountryFromPhone = phoneNumber => {
  if (!phoneNumber) return null;
  const { allCountries, allCountryCodes, iso2Lookup } = countryData;
  const clean = phoneNumber.replace(/[^\d+]/g, "");
  const prefix = clean.replace(/^\+/, "").substr(0, 4);
  if (allCountryCodes[prefix]) {
    const iso2 = allCountryCodes[prefix][0];
    if (iso2 && iso2Lookup[iso2] !== undefined) return allCountries[iso2Lookup[iso2]];
  }
  let best = { dialCode: "", priority: 10001 };
  allCountries.forEach(c => {
    const n = clean.startsWith("+") ? clean.replace(/^\+/, "") : clean;
    const d = c.dialCode.replace(/^\+/, "");
    if (n.startsWith(d)) {
      if (d.length > best.dialCode.replace(/^\+/, "").length) best = c;
      else if (d.length === best.dialCode.replace(/^\+/, "").length && c.priority < best.priority) best = c;
    }
  });
  return best.priority === 10001 ? null : best;
};

const getNumberStatus = num => {
  if (!num.nextRenewalDate) return "Active";
  return new Date(num.nextRenewalDate) >= new Date() ? "Active" : "Inactive";
};

const CountryFlag = ({ country }) => {
  if (!country?.iso2) return null;
  return (
    <div className="react-tel-input">
      <div className={`flag ${country.iso2.toLowerCase()}`} style={{ backgroundImage: `url(${Flags})` }} />
    </div>
  );
};

const exportCSV = (rows, filename) => {
  const esc = v => `"${String(v).replace(/"/g, '""')}"`;
  const blob = new Blob([rows.map(r => r.map(esc).join(",")).join("\n")], { type: "text/csv" });
  Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: filename }).click();
};

const getInitials = m => {
  const f = (m.firstName || "")[0] || "";
  const l = (m.lastName || "")[0] || "";
  return (f + l).toUpperCase() || m.email?.[0]?.toUpperCase() || "?";
};
const getFullName = m => [m.firstName, m.lastName].filter(Boolean).join(" ") || m.email || "";

const DEFAULT_FILTERS = { statuses: [], countries: [], members: [] };

// ── sub-components ────────────────────────────────────────────────────────────

const AvatarStack = ({ memberIds, members, max = 3 }) => {
  const matched = memberIds.map(id => members.find(m => m.memberId === id)).filter(Boolean);
  const shown = matched.slice(0, max);
  const extra = matched.length - max;
  return (
    <div className="mnAvatarStack">
      {shown.map((m, i) => (
        <div key={m.memberId} className="mnAvatar" style={{ marginLeft: i === 0 ? 0 : -8, zIndex: shown.length - i }}>
          {getInitials(m)}
        </div>
      ))}
      {extra > 0 && <div className="mnAvatar mnAvatar--extra" style={{ marginLeft: -8 }}>+{extra}</div>}
    </div>
  );
};

const AssignedCell = ({ assignedTo, members }) => {
  const isEmpty = !assignedTo || assignedTo.length === 0;
  if (isEmpty) {
    return (
      <div className="mnAssigned mnAssigned--all">
        <span className="mnAssigned__icon">👥</span>
        <span>All teammates</span>
      </div>
    );
  }
  const names = assignedTo.map(id => members.find(m => m.memberId === id)).filter(Boolean);
  return (
    <div className="mnAssigned">
      <AvatarStack memberIds={assignedTo} members={members} />
      <span className="mnAssigned__label">{names.length === 1 ? getFullName(names[0]) : `${names.length} people`}</span>
    </div>
  );
};

const AssignModal = ({ number, members, onClose, onSave, saving }) => {
  const isAll = !number.assignedTo || number.assignedTo.length === 0;
  const [mode, setMode] = useState(isAll ? "all" : "specific");
  const [selected, setSelected] = useState(isAll ? [] : [...(number.assignedTo || [])]);
  const toggle = id => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const handleSave = () => {
    if (mode === "specific" && selected.length === 0) return;
    onSave(number.number, number.numberType, mode === "all" ? [] : selected);
  };
  return (
    <div className="mnModal__overlay" onClick={onClose}>
      <div className="mnModal" onClick={e => e.stopPropagation()}>
        <div className="mnModal__header">
          <div className="mnModal__title">Number availability</div>
          <button className="mnModal__close" onClick={onClose}>✕</button>
        </div>
        <div className="mnModal__number">{number.number}</div>
        <div className="mnModal__modes">
          {["all", "specific"].map(m => (
            <button key={m} className={`mnModal__modeBtn ${mode === m ? "mnModal__modeBtn--active" : ""}`} onClick={() => setMode(m)}>
              {m === "all" ? "All teammates" : "Specific people"}
            </button>
          ))}
        </div>
        {mode === "all" && (
          <div className="mnModal__allNote">Available to <strong>everyone</strong> on your team.</div>
        )}
        {mode === "specific" && (
          <div className="mnModal__memberList">
            {members.map(m => {
              const checked = selected.includes(m.memberId);
              return (
                <button key={m.memberId} className={`mnModal__memberRow ${checked ? "mnModal__memberRow--selected" : ""}`} onClick={() => toggle(m.memberId)}>
                  <div className={`mnModal__check ${checked ? "mnModal__check--on" : ""}`}>{checked && <span>✓</span>}</div>
                  <div className="mnModal__avatar">{getInitials(m)}</div>
                  <span className="mnModal__memberName">{getFullName(m)}</span>
                </button>
              );
            })}
            {selected.length === 0 && <div className="mnModal__err">Select at least one teammate.</div>}
          </div>
        )}
        <div className="mnModal__actions">
          <button className="mnModal__btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="mnModal__btn mnModal__btn--primary" onClick={handleSave} disabled={saving || (mode === "specific" && selected.length === 0)}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── MultiSelectDropdown (used for Status + Team member) ─────────────────────────

const MultiSelectDropdown = ({ allLabel, options, selected, onChange, withAvatars = false }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const allSelected = selected.length === 0;
  const toggle = id => onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  const label = allSelected
    ? allLabel
    : selected.length === 1
      ? (options.find(o => o.id === selected[0])?.label || "")
      : `${selected.length} selected`;
  return (
    <div className="ccuMs" ref={ref}>
      <button type="button" className={`ccuMs__trigger ${open ? "ccuMs__trigger--open" : ""}`} onClick={() => setOpen(o => !o)}>
        <span className={allSelected ? "ccuMs__label" : "ccuMs__label ccuMs__label--active"}>{label}</span>
        <i className="material-icons" style={{ fontSize: 16, transform: open ? "rotate(180deg)" : "none" }}>expand_more</i>
      </button>
      {open && (
        <div className="ccuMs__menu">
          <button type="button" className={`ccuMs__row ${allSelected ? "ccuMs__row--on" : ""}`} onClick={() => onChange([])}>
            <span className={`ccuChk ${allSelected ? "ccuChk--on" : ""}`}>{allSelected && "✓"}</span>
            <span className="ccuMs__name">{allLabel}</span>
          </button>
          <div className="ccuMs__divider" />
          {options.map(o => {
            const checked = selected.includes(o.id);
            return (
              <button type="button" key={o.id} className={`ccuMs__row ${checked ? "ccuMs__row--on" : ""}`} onClick={() => toggle(o.id)}>
                <span className={`ccuChk ${checked ? "ccuChk--on" : ""}`}>{checked && "✓"}</span>
                {withAvatars && <span className="ccuMs__avatar">{o.initials}</span>}
                <span className="ccuMs__name">{o.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── FilterPanel ────────────────────────────────────────────────────────────────

const FilterPanel = ({ members, availableCountries, pending, setPending, onApply, onCancel, onClear }) => {
  const statusOptions = STATUS_OPTIONS.map(s => ({ id: s, label: s }));
  const memberOptions = members.map(m => ({ id: m.memberId, label: getFullName(m), initials: getInitials(m) }));
  const toggleCountry = name =>
    setPending(p => ({ ...p, countries: p.countries.includes(name) ? p.countries.filter(x => x !== name) : [...p.countries, name] }));

  return (
    <div className="ccuPanel">
      {/* Status */}
      <div className="ccuPanel__section">
        <div className="ccuPanel__label">Status</div>
        <MultiSelectDropdown
          allLabel="All statuses"
          options={statusOptions}
          selected={pending.statuses}
          onChange={v => setPending(p => ({ ...p, statuses: v }))}
        />
      </div>
      <div className="ccuPanel__divider" />

      {/* Country */}
      <div className="ccuPanel__section">
        <div className="ccuPanel__label">Country</div>
        {availableCountries.length > 0 ? (
          <div className="ccuPanel__countryList">
            {availableCountries.map(c => {
              const checked = pending.countries.includes(c.name);
              return (
                <button key={c.name} className={`ccuPanel__countryRow ${checked ? "ccuPanel__countryRow--on" : ""}`} onClick={() => toggleCountry(c.name)}>
                  <span className={`ccuChk ${checked ? "ccuChk--on" : ""}`}>{checked && "✓"}</span>
                  <CountryFlag country={c} />
                  <span className="ccuPanel__countryName">{c.name}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="ccuPanel__empty">No countries available</div>
        )}
      </div>
      <div className="ccuPanel__divider" />

      {/* Team member */}
      <div className="ccuPanel__section">
        <div className="ccuPanel__label">Team member</div>
        <MultiSelectDropdown
          allLabel="All members"
          options={memberOptions}
          selected={pending.members}
          onChange={v => setPending(p => ({ ...p, members: v }))}
          withAvatars
        />
      </div>
      <div className="ccuPanel__divider" />

      <div className="ccuPanel__footer">
        <button className="ccuPanel__clear" onClick={onClear}>Clear all</button>
        <div className="ccuPanel__footerBtns">
          <button className="ccuBtn" onClick={onCancel}>Cancel</button>
          <button className="ccuBtn ccuBtn--primary" onClick={onApply}>Apply filters</button>
        </div>
      </div>
    </div>
  );
};

// ── ManageNumbers ─────────────────────────────────────────────────────────────

const ManageNumbers = observer(() => {
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [editingNumber, setEditingNumber] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [pending, setPending] = useState(DEFAULT_FILTERS);
  const [applied, setApplied] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const filterRef = useRef(null);

  const indiaCallingEnabled = userDetail?.userFeatureFlag?.indiaCallingEnabled === true;

  // Twilio numbers (purchased + verified) — numberType "purchased" | "custom"
  const twilioNumbers = (dialerStoreData.myPhoneNumbers?.phoneNumbers || []).map(n => ({
    number: n.number,
    numberType: n.numberType === "purchased" ? "TWILIO" : "VERIFIED",
    assignedTo: n.assignedTo || [],
    monthlyCost: n.monthlyCost || 0,
    nextRenewalDate: n.nextRenewalDate || ""
  }));

  // VoBiz (India) numbers
  const voBizNumbers = (DialerServiceData.voBizPhoneList || []).map(n => ({
    number: n.number,
    numberType: "VOBIZ",
    assignedTo: n.assignedTo || [],
    monthlyCost: n.monthlyCost || 0,
    nextRenewalDate: n.nextRenewalDate || ""
  }));

  const numbers = [...twilioNumbers, ...voBizNumbers];
  const loading = dialerStoreData.myPhoneNumbers === null
    || (indiaCallingEnabled && DialerServiceData.voBizPhoneList === null);

  useEffect(() => {
    if (!dialerStoreData.myPhoneNumbers) dialerStoreData.getMyPhoneNumbers();
    if (indiaCallingEnabled && DialerServiceData.voBizPhoneList === null) {
      DialerServiceData.getVoBizPhoneNumbers();
    }
    fetchMembers();
  }, []);

  useEffect(() => {
    const h = e => { if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const fetchMembers = async () => {
    try {
      const res = await makeApi({ url: URLS.getTeamActiveList, method: "GET" });
      setMembers(res?.data || []);
    } catch (e) { console.error("Failed to fetch team members", e); }
  };

  // enrich numbers with derived fields
  const enriched = numbers.map(n => ({
    ...n,
    _country: getCountryFromPhone(n.number)?.name || "",
    _status: getNumberStatus(n)
  }));

  // available filter options (derived from actual data)
  // Country options as objects (with iso2 for flags); filtering still matches by name
  const availableCountries = (() => {
    const map = new Map();
    enriched.forEach(n => {
      const c = getCountryFromPhone(n.number);
      if (c) map.set(c.name, c);
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  })();

  // apply filters
  const filteredNumbers = enriched.filter(n => {
    if (applied.statuses.length && !applied.statuses.includes(n._status)) return false;
    if (applied.countries.length && !applied.countries.includes(n._country)) return false;
    if (applied.members.length) {
      const assignedTo = n.assignedTo || [];
      if (assignedTo.length === 0) return false; // not assigned to specific members
      if (!applied.members.some(id => assignedTo.includes(id))) return false;
    }
    return true;
  });

  // pagination
  const totalPages = Math.ceil(filteredNumbers.length / PAGE_SIZE);
  const pagedNumbers = filteredNumbers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // active chip count
  const activeCount = applied.statuses.length + applied.countries.length + applied.members.length;

  const applyFilters = () => {
    setApplied(pending);
    setPage(1);
    setFilterOpen(false);
  };

  // Discard pending changes (revert to applied) and close
  const cancelFilters = () => {
    setPending(applied);
    setFilterOpen(false);
  };

  const clearAllFilters = () => {
    setPending(DEFAULT_FILTERS);
    setApplied(DEFAULT_FILTERS);
    setPage(1);
    setFilterOpen(false);
  };

  const openFilter = () => {
    setPending(applied);
    setFilterOpen(true);
  };

  // chips for applied filters
  const chips = [
    ...applied.statuses.map(s => ({ key: `s_${s}`, label: s })),
    ...applied.countries.map(c => ({ key: `c_${c}`, label: c })),
    ...applied.members.map(id => {
      const m = members.find(x => x.memberId === id);
      return { key: `m_${id}`, label: m ? getFullName(m) : id };
    })
  ];

  const removeChip = key => {
    const f = { ...applied };
    if (key.startsWith("s_")) f.statuses = f.statuses.filter(x => `s_${x}` !== key);
    else if (key.startsWith("c_")) f.countries = f.countries.filter(x => `c_${x}` !== key);
    else if (key.startsWith("m_")) f.members = f.members.filter(x => `m_${x}` !== key);
    setApplied(f);
    setPending(f);
    setPage(1);
  };

  const allChecked = selected.size === pagedNumbers.length && pagedNumbers.length > 0;
  const someChecked = selected.size > 0 && selected.size < pagedNumbers.length;
  const toggleRow = num => setSelected(prev => { const next = new Set(prev); next.has(num) ? next.delete(num) : next.add(num); return next; });
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(pagedNumbers.map(n => n.number)));

  // buy number flow
  const purchaseCreditsCbk = async (amount, stripeToken) => {
    if (amount && !stripeToken) {
      transactionAnimationState.setShowPopup(true);
      await dialerStoreData.purchaseCredits({ amount });
      transactionAnimationState.setShowPopup(false);
    } else if (amount && stripeToken) {
      transactionAnimationState.setShowPopup(true);
      await dialerStoreData.purchaseCreditsWithoutSubscription({ chargeAmount: amount, stripeToken });
      transactionAnimationState.setShowPopup(false);
    }
  };

  const openBuyCredits = async () => {
    if (await checkTeamSubscriptionActive()) {
      const cardResponse = Utils.checkCustomerIdAvailable();
      if (cardResponse === 200) { buyCreditsPopupState.setNewPayment(false); buyCreditsPopupState.setCallBack(purchaseCreditsCbk); buyCreditsPopupState.setPopupValue({ type: "DIALER" }); buyCreditsPopupState.setShowPopup(true); }
      else if (Utils.isEligibleToShowPaymentPopup(cardResponse)) { buyCreditsPopupState.setNewPayment(true); buyCreditsPopupState.setCallBack(purchaseCreditsCbk); buyCreditsPopupState.setPopupValue({ type: "DIALER" }); buyCreditsPopupState.setShowPopup(true); }
      else if (cardResponse === 203) { upgradePopupState.setPopupValues({ type: "data_AddCreditsToDialer" }); upgradePopupState.setPopup(true); }
    }
  };

  const purchasePhoneNumber = async (ph_number, token) => {
    transactionAnimationState.setShowPopup(true);
    await dialerStoreData.purchaseNumber({ ph_number, token }, openBuyCredits);
    transactionAnimationState.setShowPopup(false);
  };

  const purchaseCallBack = (selectedPhoneNumber, key) => {
    if (key !== "back") purchasePhoneNumber(selectedPhoneNumber);
  };

  const handleBuyNumber = async () => {
    const { dialerEnabled = false } = userDetail?.userFeatureFlag || {};
    if (!dialerEnabled) { Utils.showMessagePopup("Access to buy new number is denied. Please contact your administrator"); return; }
    if (await checkTeamSubscriptionActive()) {
      buyNewNumberPopupState.setCallBack(purchaseCallBack);
      buyNewNumberPopupState.setShowPopup(true);
    }
  };

  const handleSave = async (number, numberType, assignedTo) => {
    setSaving(true);
    const ok = await dialerStoreData.updateNumberAssignment(number, numberType, assignedTo);
    setSaving(false);
    if (ok) setEditingNumber(null);
  };

  const handleExport = () => {
    const rows = filteredNumbers.filter(n => selected.has(n.number));
    if (!rows.length) return;
    const headers = ["Number", "Type", "Country", "Monthly Cost", "Available To", "Status"];
    const data = rows.map(n => {
      const isEmpty = !n.assignedTo || n.assignedTo.length === 0;
      const assignedLabel = isEmpty ? "All teammates" : n.assignedTo.map(id => members.find(m => m.memberId === id)).filter(Boolean).map(getFullName).join("; ");
      return [n.number, n.numberType, n._country, n.monthlyCost ? `$${n.monthlyCost}` : "", assignedLabel, n._status];
    });
    exportCSV([headers, ...data], "salesgear_numbers.csv");
  };

  if (loading) {
    return (
      <div className="mnWrapper">
        {[...Array(4)].map((_, i) => <span key={i} className="linear-background" style={{ width: "100%", height: "52px", borderRadius: "8px", display: "block", marginBottom: "12px" }} />)}
      </div>
    );
  }

  return (
    <>
      {editingNumber && (
        <AssignModal number={editingNumber} members={members} onClose={() => !saving && setEditingNumber(null)} onSave={handleSave} saving={saving} />
      )}

      <div className="mnWrapper">
        {/* Toolbar */}
        <div className="mnToolbar">
          <div className="mnToolbar__left">
            <div className="mnToolbar__title">Calling numbers</div>
            <div className="mnToolbar__subtitle">Control who can call from each purchased number</div>
          </div>
          <div className="mnToolbar__right">
            <button className="mnBtn" disabled={selected.size === 0} onClick={handleExport}>
              <i className="material-icons-outlined" style={{ fontSize: 14 }}>download</i>
              Export CSV{selected.size > 0 ? ` (${selected.size})` : ""}
            </button>
            {/* Filters */}
            <div className="ccuFilterWrap" ref={filterRef}>
              <button className={`mnBtn ${filterOpen || activeCount > 0 ? "mnBtn--filterActive" : ""}`} onClick={() => filterOpen ? setFilterOpen(false) : openFilter()}>
                <i className="material-icons-outlined" style={{ fontSize: 14 }}>tune</i>
                Filters
                {activeCount > 0 && <span className="ccuBadge">{activeCount}</span>}
              </button>
              {filterOpen && (
                <FilterPanel
                  members={members}
                  availableCountries={availableCountries}
                  pending={pending}
                  setPending={setPending}
                  onApply={applyFilters}
                  onCancel={cancelFilters}
                  onClear={clearAllFilters}
                />
              )}
            </div>
            <button className="mnBtn mnBtn--primary" onClick={handleBuyNumber}>+ Buy number</button>
          </div>
        </div>

        {/* Active filter chips */}
        {chips.length > 0 && (
          <div className="mnChips">
            {chips.map(chip => (
              <span key={chip.key} className="ccuChip">
                {chip.label}
                <button onClick={() => removeChip(chip.key)}>×</button>
              </span>
            ))}
            <button className="mnChips__clear" onClick={clearAllFilters}>Clear all</button>
          </div>
        )}

        {/* Table */}
        <div className="mnTable__wrap">
          <table className="mnTable">
            <thead>
              <tr>
                <th className="mnTh mnTh--check">
                  <input type="checkbox" checked={allChecked} ref={el => { if (el) el.indeterminate = someChecked; }} onChange={toggleAll} className="mnCheckbox" />
                </th>
                <th className="mnTh">Number</th>
                <th className="mnTh">Type</th>
                <th className="mnTh">Country</th>
                <th className="mnTh">Monthly cost</th>
                <th className="mnTh">Available to</th>
                <th className="mnTh">Status</th>
                <th className="mnTh" />
              </tr>
            </thead>
            <tbody>
              {pagedNumbers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="mnTd mnTd--empty">
                    <i className="material-icons-outlined" style={{ fontSize: 32, color: "#d1d5db", display: "block", marginBottom: 8 }}>phone_iphone</i>
                    {filteredNumbers.length === 0 && numbers.length > 0
                      ? "No numbers match your filters"
                      : "No phone numbers purchased yet"}
                  </td>
                </tr>
              ) : (
                pagedNumbers.map(num => {
                  const { number = "", numberType = "", assignedTo = [], monthlyCost = 0, _status = "Active" } = num;
                  const country = getCountryFromPhone(number);
                  const isSelected = selected.has(number);
                  const typeMeta = NUMBER_TYPE_META[numberType] || { label: numberType, cls: "custom" };
                  return (
                    <tr key={number} className={`mnTr ${isSelected ? "mnTr--selected" : ""}`}>
                      <td className="mnTd mnTd--check">
                        <input type="checkbox" checked={isSelected} onChange={() => toggleRow(number)} className="mnCheckbox" />
                      </td>
                      <td className="mnTd">
                        <div className="mnNumber">{number}</div>
                      </td>
                      <td className="mnTd">
                        <span className={`mnPill mnPill--${typeMeta.cls}`}>{typeMeta.label}</span>
                      </td>
                      <td className="mnTd">
                        {country ? (
                          <div className="mnCountry">
                            <CountryFlag country={country} />
                            <div>
                              <div className="mnCountry__name">{country.name}</div>
                              <div className="mnCountry__dial">+{country.dialCode}</div>
                            </div>
                          </div>
                        ) : "—"}
                      </td>
                      <td className="mnTd">
                        {monthlyCost ? <span className="mnCost">${monthlyCost.toFixed(2)}<span className="mnCost__unit">/mo</span></span> : "—"}
                      </td>
                      <td className="mnTd">
                        <AssignedCell assignedTo={assignedTo} members={members} />
                      </td>
                      <td className="mnTd">
                        <span className={`mnPill mnPill--${_status === "Active" ? "active" : "inactive"}`}>{_status}</span>
                      </td>
                      <td className="mnTd mnTd--action">
                        <button className="mnBtn mnBtn--sm" onClick={() => setEditingNumber(num)}>Edit</button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="ccuPagination">
            <span className="ccuPagination__info">{filteredNumbers.length} number{filteredNumbers.length !== 1 ? "s" : ""}</span>
            <button className="mnBtn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <i className="material-icons-outlined" style={{ fontSize: 14 }}>chevron_left</i>
            </button>
            <span className="ccuPagination__info">Page {page} of {totalPages}</span>
            <button className="mnBtn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              <i className="material-icons-outlined" style={{ fontSize: 14 }}>chevron_right</i>
            </button>
          </div>
        )}

        <div className="mnFootnote">
          <i className="material-icons-outlined">info</i>
          By default all numbers are available to the entire team. Use Edit to restrict access.
        </div>
      </div>
    </>
  );
});

export default ManageNumbers;
